import React, { useMemo } from 'react';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { ColorStyleProps } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import { Trans } from './localization';
import { ResultsActionsContainer } from './ResultsActionsContainer';
import { ResultsCountsMetricBlueStar } from './ResultsCountsMetricBlueStar';
import { ResultsCountsSelectedWell } from './ResultsCountsSelectedWell';
import { ResultsSelectedCountsMetric } from './ResultsSelectedCountsMetric';
import { Text } from './themeDefinitions';

export interface Props {
  /**
   * A custom label to describe each row. Defaults to "Items".
   */
  itemsLabel?: string;
  /**
   * The number of rows to be displayed in the list. If the list is filtered, this includes only
   * rows that are included in the filtered list.
   */
  rowCount: number;
  /**
   * The sum of counts for all rows displayed in the list. If the list is filtered, this includes
   * only counts for rows that are included in the filtered list.
   */
  sumOfCounts?: number;
  /**
   * If the list is filtered, the total number of rows, including any that are currently filtered
   * from view. Leave this prop undefined if the list is not being filtered.
   */
  totalRowCount?: number;
  /**
   * Use this flag to not default totalRowCount to rowCount when rowCount is not provided
   */
  skipDefaultTotalRowCount?: boolean;
  /**
   * The sum of counts for all rows, including any that are currently filtered from view. Leave this
   * prop undefined if the list is not being filtered.
   */
  totalSumOfCounts?: number;
  /**
   * The number of selected rows.
   */
  selectedRowCount?: number;
  /**
   * The sum of counts for the selected rows.
   */
  selectedSumOfCounts?: number;
  /**
   * Actions that operate only on the selected rows. Each element in this array is expected to
   * render a small Button (or Button-like component).
   *
   * These actions are only displayed if there is at least one selected item.
   */
  rowActions?: React.ReactNode[];
  /**
   * The background to use for the well containing count(s) and action(s) for selected items.
   * Defaults to `ThemeColors.FillLevel1`, which is appropriate when the component is used inside a
   * standard well.
   */
  selectedWellBg?: ColorStyleProps['bg'];
}

interface CountBoxProps {
  children: string;
}
const CountBox = ({ children }: CountBoxProps) => (
  <Box as="span" color={Text} fontWeight={ThemeFontWeights.Bold}>
    {children}
  </Box>
);

/**
 * Count(s) and action(s) displayed within a Results Header.
 */
export const ResultsHeaderControls = ({
  rowCount,
  sumOfCounts,
  itemsLabel,
  rowActions,
  totalRowCount: totalRowCountProp,
  skipDefaultTotalRowCount,
  selectedRowCount,
  selectedSumOfCounts,
  selectedWellBg,
  totalSumOfCounts,
}: Props) => {
  let totalRowCount = totalRowCountProp;
  if (typeof totalRowCountProp === 'undefined' && !skipDefaultTotalRowCount) {
    totalRowCount = rowCount;
  }

  const hasRowActions = rowActions && rowActions.length > 0;
  const hasSelection = typeof selectedRowCount === 'number' && selectedRowCount > 0;

  const displayedRowTooltip = useMemo(() => {
    if (typeof sumOfCounts !== 'number') return undefined;

    if (typeof totalSumOfCounts !== 'number') {
      return (
        <Trans count={sumOfCounts}>
          <CountBox>{'{{ count, INTEGER }}'}</CountBox> Sum of Counts
        </Trans>
      );
    }
    return (
      <Box textAlign="left">
        <Trans count={sumOfCounts}>
          <CountBox>{'{{ count, INTEGER }}'}</CountBox> Sum of Counts (Filtered)
        </Trans>
        <Trans count={totalSumOfCounts}>
          <CountBox>{'{{ count, INTEGER }}'}</CountBox> Sum of Counts (Total)
        </Trans>
      </Box>
    );
  }, [sumOfCounts, totalSumOfCounts]);

  const selectedCountTooltip = useMemo(
    () =>
      typeof selectedSumOfCounts === 'number' ? (
        <Trans count={selectedSumOfCounts}>
          <CountBox>{'{{ count, INTEGER }}'}</CountBox> Sum of Counts
        </Trans>
      ) : undefined,
    [selectedSumOfCounts],
  );

  return (
    <Stack itemSpacing={2} alignItems="center" minHeight={4}>
      <ResultsCountsMetricBlueStar
        count={rowCount}
        totalCount={totalRowCount}
        itemsLabel={itemsLabel}
        tooltipContent={displayedRowTooltip}
      />
      {hasSelection && (
        <ResultsCountsSelectedWell bg={selectedWellBg}>
          <Stack itemSpacing={2} alignItems="center">
            <ResultsSelectedCountsMetric
              count={selectedRowCount}
              tooltipContent={selectedCountTooltip}
            />
            {hasRowActions && <ResultsActionsContainer>{rowActions}</ResultsActionsContainer>}
          </Stack>
        </ResultsCountsSelectedWell>
      )}
    </Stack>
  );
};
